import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { MaterialReactTable, type MRT_ColumnDef } from 'material-react-table';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import { Capacitor } from '@capacitor/core';

import { DeviceTypeToDisplayStringMap } from '../../../common/types/device';
import { useAuthenticator } from '../../auth/AuthenticationContext';
import { ActiveAlert, useAlertsManager } from '../../system/AlertsManager';
import { useIsSmallScreen } from '../../system/SystemMediaQueriesProvider';
import { useInjectableComponents } from '../../system/services/injectableComponentsManager';
import { Property, usePropertyQuery } from '../../../types/generated-types';
import { basePropertyMenuItems } from './ui/base-property-context-menu';

export interface OfflineDeviceReportRow {
  id: string;
  propertyId: string;
  propertyName: string;
  unitId: string;
  unitName: string;
  deviceId: string;
  deviceName: string;
  deviceSummary?: string;
  deviceType: string;
  initiated?: number;
  offlineMs: number;
}

export interface OfflineDevicePropertyGroup {
  propertyId: string;
  propertyName: string;
  longestOfflineMs: number;
  rows: OfflineDeviceReportRow[];
}

interface DeviceOfflineReportProps {
  scope?: 'all' | 'property';
}

const REPORT_COLUMN_WIDTHS = {
  unit: 180,
  device: 220,
  deviceType: 260,
  initiated: 210,
  outageDuration: 228,
} as const;

const REPORT_DEFAULT_PAGE_SIZE = 25;
const REPORT_ROWS_PER_PAGE_OPTIONS: Array<{ label: string; value: number }> = [
  { label: '25', value: 25 },
  { label: '50', value: 50 },
  { label: '100', value: 100 },
  { label: 'All', value: -1 },
];

const REPORT_CONTENT_WIDTH = Object.values(REPORT_COLUMN_WIDTHS).reduce(
  (sum, width) => sum + width,
  0,
);

type CsvWritableFile = {
  write: (data: Blob) => Promise<void>;
  close: () => Promise<void>;
};

type CsvFileHandle = {
  createWritable: () => Promise<CsvWritableFile>;
};

type WindowWithSaveFilePicker = Window &
  typeof globalThis & {
    showSaveFilePicker?: (options: {
      suggestedName: string;
      types: Array<{
        description: string;
        accept: Record<string, string[]>;
      }>;
    }) => Promise<CsvFileHandle>;
  };

function normalizeTimestamp(timestamp?: number | null): number | undefined {
  if (!timestamp || Number.isNaN(timestamp)) return undefined;
  return timestamp < 10000000000 ? timestamp * 1000 : timestamp;
}

function formatTimestamp(timestamp?: number): string {
  if (!timestamp) return 'Unknown';

  return new Date(timestamp).toLocaleString(undefined, {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatDuration(milliseconds: number): string {
  if (!Number.isFinite(milliseconds) || milliseconds < 0) return 'Unknown';

  const minutes = Math.floor(milliseconds / 60000);
  const days = Math.floor(minutes / 1440);
  const hours = Math.floor((minutes % 1440) / 60);
  const remainingMinutes = minutes % 60;

  if (days > 0) {
    return `${days}d ${hours}h`;
  }

  if (hours > 0) {
    return `${hours}h ${remainingMinutes}m`;
  }

  return `${remainingMinutes}m`;
}

function getOfflineDurationParts(milliseconds: number): {
  days?: number;
  hours?: number;
} {
  if (!Number.isFinite(milliseconds) || milliseconds < 0) return {};

  const days = Math.floor(milliseconds / 86400000);
  const hours = Math.floor((milliseconds % 86400000) / 3600000);

  return { days, hours };
}

function formatDays(days?: number): string {
  if (!days) return '';
  return `${days} ${days === 1 ? 'day' : 'days'}`;
}

function formatHours(hours?: number): string {
  if (hours === undefined) return '';
  return `${hours} ${hours === 1 ? 'hour' : 'hours'}`;
}

function formatAbbreviatedHours(hours?: number): string {
  if (hours === undefined) return '';
  return `${hours} ${hours === 1 ? 'hr' : 'hrs'}`;
}

function escapeCsvField(value?: string | number): string {
  const text = value === undefined ? '' : String(value);
  if (!/[",\r\n]/.test(text)) return text;

  return `"${text.replace(/"/g, '""')}"`;
}

function serializeCsvRow(values: Array<string | number | undefined>): string {
  return values.map(escapeCsvField).join(',');
}

export function buildDeviceOfflineReportCsv(
  propertyGroups: OfflineDevicePropertyGroup[],
): string {
  const header = serializeCsvRow([
    'Property',
    'Unit',
    'Device',
    'Device Type',
    'Went Offline',
    'Outage Duration Days',
    'Outage Duration Hours',
  ]);

  const rows = propertyGroups.flatMap((propertyGroup) =>
    propertyGroup.rows.map((row) => {
      const { days, hours } = getOfflineDurationParts(row.offlineMs);

      return serializeCsvRow([
        row.propertyName || propertyGroup.propertyName,
        row.unitName,
        row.deviceName,
        row.deviceType,
        formatTimestamp(row.initiated),
        formatDays(days),
        formatHours(hours),
      ]);
    }),
  );

  return [header, ...rows, ''].join('\r\n');
}

function getCsvFilename(
  propertyName: string,
  isPropertyScope: boolean,
): string {
  const name = isPropertyScope ? propertyName : 'all-properties';
  const slug =
    name
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'offline-devices';

  return `${slug}-offline-device-report.csv`;
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');

  anchor.href = url;
  anchor.download = filename;
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

async function saveCsvBlob(blob: Blob, filename: string) {
  const showSaveFilePicker = (window as WindowWithSaveFilePicker)
    .showSaveFilePicker;

  if (showSaveFilePicker) {
    try {
      const fileHandle = await showSaveFilePicker({
        suggestedName: filename,
        types: [
          {
            description: 'CSV file',
            accept: {
              'text/csv': ['.csv'],
            },
          },
        ],
      });
      const writable = await fileHandle.createWritable();
      await writable.write(blob);
      await writable.close();
      return;
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        return;
      }

      throw error;
    }
  }

  downloadBlob(blob, filename);
}

function OutageDurationHeader({ label }: { label: string }) {
  return (
    <Typography
      component="div"
      sx={{ fontSize: 14, fontWeight: 700, textAlign: 'right', width: '100%' }}
    >
      {label}
    </Typography>
  );
}

function OutageDurationCell({
  isSmall,
  milliseconds,
}: {
  isSmall: boolean;
  milliseconds: number;
}) {
  const { days, hours } = getOfflineDurationParts(milliseconds);

  return (
    <Box
      sx={{
        display: 'grid',
        gap: 0.75,
        gridTemplateColumns: isSmall ? 'max-content max-content' : '82px 64px',
        marginLeft: 'auto',
        width: 'fit-content',
      }}
    >
      <Typography component="span" sx={{ fontSize: 14, textAlign: 'right' }}>
        {formatDays(days)}
      </Typography>
      <Typography component="span" sx={{ fontSize: 14, textAlign: 'right' }}>
        {formatAbbreviatedHours(hours)}
      </Typography>
    </Box>
  );
}

function formatDeviceType(deviceType?: string): string {
  if (!deviceType) return 'Unknown type';
  return DeviceTypeToDisplayStringMap[deviceType] ?? deviceType;
}

function getOfflineDeviceRows(
  alerts: ActiveAlert[],
  now: number,
): OfflineDeviceReportRow[] {
  return alerts
    .filter((alert) => alert.type?.id === 'DEVICE_OFFLINE')
    .map((alert) => {
      const initiated = normalizeTimestamp(alert.initiated);
      const offlineMs = initiated ? now - initiated : -1;

      return {
        id: alert._id,
        propertyId: alert.property?.id ?? 'unknown-property',
        propertyName: alert.property?.name ?? 'Unknown Property',
        unitId: alert.unit?.id ?? '',
        unitName: alert.unit?.name ?? 'Unknown Unit',
        deviceId: alert.device?.id ?? '',
        deviceName: alert.device?.name ?? 'Unknown Device',
        deviceType: formatDeviceType(alert.device?.type ?? undefined),
        initiated,
        offlineMs,
      };
    })
    .sort((a, b) => b.offlineMs - a.offlineMs);
}

export function getOfflineDevicePropertyGroups(
  rows: OfflineDeviceReportRow[],
): OfflineDevicePropertyGroup[] {
  const groups = new Map<string, OfflineDevicePropertyGroup>();

  rows.forEach((row) => {
    const groupKey = row.propertyId || row.propertyName;
    const group =
      groups.get(groupKey) ??
      ({
        propertyId: row.propertyId,
        propertyName: row.propertyName,
        longestOfflineMs: row.offlineMs,
        rows: [],
      } as OfflineDevicePropertyGroup);

    group.rows.push(row);
    group.longestOfflineMs = Math.max(group.longestOfflineMs, row.offlineMs);
    groups.set(groupKey, group);
  });

  return Array.from(groups.values())
    .map((group) => ({
      ...group,
      rows: group.rows.sort((a, b) => b.offlineMs - a.offlineMs),
    }))
    .sort((a, b) => a.propertyName.localeCompare(b.propertyName));
}

export function DeviceOfflineReport(props: DeviceOfflineReportProps) {
  const { scope = 'all' } = props;
  const { id: propertyId } = useParams<{ id: string }>();
  const { pathname } = useLocation();
  const { user } = useAuthenticator();
  const {
    setContextMenuItems,
    setSubtitle,
    setSubtitlePath,
    setTitle,
    setTitlePath,
  } = useInjectableComponents();
  const { alerts, loading } = useAlertsManager();
  const [now, setNow] = useState(() => Date.now());
  const [expandedPropertyIds, setExpandedPropertyIds] = useState<string[]>([]);
  const initializedExpandedProperties = useRef(false);

  const isPropertyScope = scope === 'property';
  const isSuperUser = user?.isSuper || false;
  const isSmall = useIsSmallScreen();

  const { data: propertyData, loading: propertyLoading } = usePropertyQuery({
    variables: { _id: propertyId },
    skip: !isPropertyScope || !propertyId,
    fetchPolicy: 'cache-first',
  });

  const rows = useMemo(() => {
    const allRows = getOfflineDeviceRows(Object.values(alerts.alert), now);

    if (!isPropertyScope) {
      return allRows;
    }

    return allRows.filter((row) => row.propertyId === propertyId);
  }, [alerts.alert, isPropertyScope, now, propertyId]);

  const propertyGroups = useMemo(
    () => getOfflineDevicePropertyGroups(rows),
    [rows],
  );
  const propertyGroupIds = useMemo(
    () => propertyGroups.map((group) => group.propertyId),
    [propertyGroups],
  );
  const allPropertiesExpanded =
    propertyGroupIds.length > 0 &&
    propertyGroupIds.every((id) => expandedPropertyIds.includes(id));
  const propertyName =
    propertyData?.property?.title ??
    propertyGroups[0]?.propertyName ??
    'Property';
  const reportLoading = loading || (isPropertyScope && propertyLoading);
  const showCsvExport = !Capacitor.isNativePlatform();

  const columns = useMemo<MRT_ColumnDef<OfflineDeviceReportRow>[]>(() => {
    const mobileOverflowSx = {
      minWidth: 0,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    };
    const applyMobileColumnLayout = (
      column: MRT_ColumnDef<OfflineDeviceReportRow>,
    ): MRT_ColumnDef<OfflineDeviceReportRow> => {
      const bodyCellProps =
        typeof column.muiTableBodyCellProps === 'function'
          ? undefined
          : column.muiTableBodyCellProps;
      const headCellProps =
        typeof column.muiTableHeadCellProps === 'function'
          ? undefined
          : column.muiTableHeadCellProps;

      return isSmall
        ? ({
            ...column,
            grow: true,
            minSize: 0,
            size: 1,
            muiTableBodyCellProps: {
              ...bodyCellProps,
              sx: {
                ...bodyCellProps?.sx,
                ...mobileOverflowSx,
              },
            },
            muiTableHeadCellProps: {
              ...headCellProps,
              sx: {
                ...headCellProps?.sx,
                ...mobileOverflowSx,
              },
            },
          } as MRT_ColumnDef<OfflineDeviceReportRow>)
        : column;
    };

    const deviceColumn: MRT_ColumnDef<OfflineDeviceReportRow> = {
      accessorKey: 'deviceName',
      header: 'Device',
      minSize: 160,
      size: REPORT_COLUMN_WIDTHS.device,
    };
    const mobileDeviceColumn: MRT_ColumnDef<OfflineDeviceReportRow> = {
      accessorKey: 'deviceSummary',
      header: 'Device',
      minSize: 0,
      size: 1,
      Cell: ({ row }) => (
        <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
          <Typography
            component="div"
            sx={{
              fontSize: 13,
              lineHeight: 1.2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {row.original.unitName}
          </Typography>
          <Typography
            color="text.secondary"
            component="div"
            sx={{
              fontSize: 12,
              lineHeight: 1.2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {row.original.deviceName}
          </Typography>
        </Box>
      ),
    };
    const reportColumns: MRT_ColumnDef<OfflineDeviceReportRow>[] = [
      {
        accessorKey: 'unitName',
        header: 'Unit',
        minSize: 140,
        size: REPORT_COLUMN_WIDTHS.unit,
      },
      {
        accessorKey: 'deviceType',
        header: isSmall ? 'Type' : 'Device Type',
        minSize: 170,
        size: REPORT_COLUMN_WIDTHS.deviceType,
      },
      {
        accessorKey: 'initiated',
        header: isSmall ? 'Offline' : 'Went Offline',
        minSize: 170,
        size: REPORT_COLUMN_WIDTHS.initiated,
        Cell: ({ cell }) => formatTimestamp(cell.getValue<number>()),
      },
      {
        accessorKey: 'offlineMs',
        header: isSmall ? 'Duration' : 'Outage Duration',
        Header: () => (
          <OutageDurationHeader
            label={isSmall ? 'Duration' : 'Outage Duration'}
          />
        ),
        minSize: 216,
        size: REPORT_COLUMN_WIDTHS.outageDuration,
        maxSize: 252,
        Cell: ({ cell }) => (
          <OutageDurationCell
            isSmall={isSmall}
            milliseconds={cell.getValue<number>()}
          />
        ),
        muiTableBodyCellProps: {
          sx: {
            justifyContent: 'flex-end',
          },
        },
        muiTableHeadCellProps: {
          sx: {
            '& .Mui-TableHeadCell-Content': {
              justifyContent: 'flex-end',
            },
          },
        },
      },
    ];

    const visibleColumns = isSmall
      ? [mobileDeviceColumn, reportColumns[1], reportColumns[3]]
      : [reportColumns[0], deviceColumn, ...reportColumns.slice(1)];

    return visibleColumns.map(applyMobileColumnLayout);
  }, [isSmall]);

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!initializedExpandedProperties.current && propertyGroupIds.length) {
      setExpandedPropertyIds(propertyGroupIds);
      initializedExpandedProperties.current = true;
    }
  }, [propertyGroupIds]);

  useEffect(() => {
    if (isPropertyScope) {
      const titlePath = propertyId
        ? `/properties/${propertyId}/summary`
        : undefined;

      setTitle(propertyName);
      setTitlePath(titlePath);
      setSubtitle('Offline Device Report');
      setSubtitlePath(titlePath);
      setContextMenuItems(
        propertyId ? basePropertyMenuItems(propertyId, pathname) : undefined,
        propertyData?.property as Property | undefined,
      );
    } else {
      setTitle('Device Offline Report');
      setTitlePath(undefined);
      setSubtitle('All Properties');
      setSubtitlePath(undefined);
      setContextMenuItems(undefined);
    }

    return () => {
      setTitle(undefined);
      setTitlePath(undefined);
      setSubtitle(undefined);
      setSubtitlePath(undefined);
      setContextMenuItems(undefined);
    };
  }, [
    isPropertyScope,
    pathname,
    propertyData,
    propertyId,
    propertyName,
    setContextMenuItems,
    setSubtitle,
    setSubtitlePath,
    setTitle,
    setTitlePath,
  ]);

  function toggleProperty(propertyGroupId: string, expanded: boolean) {
    setExpandedPropertyIds((current) => {
      if (expanded) {
        return current.includes(propertyGroupId)
          ? current
          : [...current, propertyGroupId];
      }

      return current.filter((id) => id !== propertyGroupId);
    });
  }

  function toggleAllProperties() {
    setExpandedPropertyIds(allPropertiesExpanded ? [] : propertyGroupIds);
  }

  function exportCsv() {
    const csv = buildDeviceOfflineReportCsv(propertyGroups);
    const filename = getCsvFilename(propertyName, isPropertyScope);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });

    void saveCsvBlob(blob, filename).catch((error) => {
      console.error('Unable to export device offline report CSV', error);
    });
  }

  if (!isSuperUser) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="warning">
          This report is available to super users.
        </Alert>
      </Box>
    );
  }

  if (reportLoading) {
    return (
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          flex: 1,
          flexDirection: 'column',
          justifyContent: 'center',
          p: 2,
        }}
      >
        <CircularProgress color="secondary" />
        <Typography sx={{ mt: 2 }} color="secondary" variant="h6">
          Loading device offline report...
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
        gap: 1,
        alignItems: 'center',
        minHeight: 0,
        overflow: 'auto',
        p: { xs: 1, sm: 2 },
      }}
    >
      <Stack
        aria-label="Report actions"
        role="toolbar"
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        sx={{
          alignItems: 'center',
          justifyContent: 'flex-end',
          maxWidth: REPORT_CONTENT_WIDTH,
          width: '100%',
        }}
      >
        {showCsvExport ? (
          <Button
            color="secondary"
            fullWidth={isSmall}
            size="small"
            startIcon={<DownloadOutlinedIcon />}
            variant="outlined"
            onClick={exportCsv}
          >
            Export CSV
          </Button>
        ) : null}
        <Button
          color="secondary"
          fullWidth={isSmall}
          size="small"
          startIcon={
            allPropertiesExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />
          }
          variant="outlined"
          onClick={toggleAllProperties}
          sx={{
            width: isSmall ? '100%' : 145,
          }}
        >
          {allPropertiesExpanded ? 'Collapse All' : 'Expand All'}
        </Button>
      </Stack>

      {propertyGroups.length === 0 ? (
        <Alert severity="success" sx={{ maxWidth: REPORT_CONTENT_WIDTH }}>
          {isPropertyScope
            ? 'No offline devices are currently reporting for this property.'
            : 'No offline devices are currently reporting.'}
        </Alert>
      ) : (
        propertyGroups.map((propertyGroup) => {
          const isExpanded = expandedPropertyIds.includes(
            propertyGroup.propertyId,
          );
          const reportNeedsPagination =
            propertyGroup.rows.length > REPORT_DEFAULT_PAGE_SIZE;

          return (
            <Accordion
              key={propertyGroup.propertyId}
              expanded={isExpanded}
              onChange={(_, expanded) =>
                toggleProperty(propertyGroup.propertyId, expanded)
              }
              disableGutters
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                boxShadow:
                  '0px 2px 1px -1px rgba(0, 0, 0, 0.2), 0px 1px 1px 0px rgba(0, 0, 0, 0.14), 0px 1px 3px 0px rgba(0, 0, 0, 0.12)',
                maxWidth: REPORT_CONTENT_WIDTH,
                width: '100%',
                '&:before': { display: 'none' },
              }}
            >
              <AccordionSummary
                expandIcon={
                  <ExpandMoreIcon
                    sx={{
                      backgroundColor: 'rgb(54, 175, 165)',
                      color: 'white',
                      width: '24px',
                      height: '24px',
                      borderRadius: '5px',
                    }}
                  />
                }
                sx={{
                  '--offline-report-summary-gap': isSmall ? '1px' : '8px',
                  borderBottom: '2px solid',
                  borderColor: 'divider',
                  '& .MuiAccordionSummary-content': {
                    alignItems: 'baseline',
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 'var(--offline-report-summary-gap)',
                  },
                }}
              >
                <Typography
                  component={isSmall ? 'h5' : 'p'}
                  sx={{
                    fontSize: isSmall ? 18 : undefined,
                    fontWeight: isSmall ? 800 : 700,
                  }}
                >
                  {propertyGroup.propertyName}
                </Typography>
                <Typography color="text.secondary">
                  {isSmall ? '' : '· '}
                  {propertyGroup.rows.length}{' '}
                  {propertyGroup.rows.length === 1 ? 'device' : 'devices'}
                  {' · longest offline '}
                  {formatDuration(propertyGroup.longestOfflineMs)}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                <MaterialReactTable
                  columns={columns}
                  data={propertyGroup.rows}
                  enableBottomToolbar={reportNeedsPagination}
                  enableColumnActions={false}
                  enableColumnFilters={false}
                  enableDensityToggle={false}
                  enableFullScreenToggle={false}
                  enableHiding={false}
                  enablePagination={reportNeedsPagination}
                  enableTopToolbar={false}
                  layoutMode={isSmall ? 'grid' : 'grid-no-grow'}
                  initialState={{
                    density: 'compact',
                    pagination: {
                      pageIndex: 0,
                      pageSize: REPORT_DEFAULT_PAGE_SIZE,
                    },
                    sorting: [{ id: 'offlineMs', desc: true }],
                  }}
                  muiPaginationProps={{
                    rowsPerPageOptions: REPORT_ROWS_PER_PAGE_OPTIONS,
                  }}
                  muiTableBodyProps={{
                    sx: {
                      '& tr:nth-of-type(even)': {
                        backgroundColor: 'rgba(241, 241, 241, 0.35)',
                      },
                    },
                  }}
                  muiTableContainerProps={
                    isPropertyScope
                      ? undefined
                      : {
                          sx: {
                            maxHeight: '55vh',
                          },
                        }
                  }
                  muiTableHeadCellProps={{
                    sx: {
                      fontSize: '14px',
                      fontWeight: 700,
                    },
                  }}
                  muiTableHeadRowProps={{
                    sx: {
                      backgroundColor: 'action.hover',
                    },
                  }}
                  muiTablePaperProps={{
                    sx: {
                      boxShadow: 'none',
                    },
                  }}
                  state={{
                    isLoading: loading,
                  }}
                />
              </AccordionDetails>
            </Accordion>
          );
        })
      )}
    </Box>
  );
}
